/**
 * Single source of nav truth for the Intercom AppShell.
 *
 * Mirrors the server-side `/api/me/navigation` design in docs/GRANULA_MASTER_PLAN.md.
 * Each item declares the capability it requires; the shell renders only what the
 * signed-in user's role grants (see store/org.ts). Until the multi-tenant backend
 * ships, capabilities are derived client-side from the auth user (+ a ?role= preview
 * override) so the RBAC-driven nav is already demonstrable.
 */
import {
  Home01Icon,
  Layers01Icon,
  Task01Icon,
  UserMultipleIcon,
  Wallet01Icon,
  Analytics01Icon,
  SparklesIcon,
  Settings01Icon,
  UserGroupIcon,
  Calendar03Icon,
  ClipboardIcon,
  Invoice01Icon,
  Coins01Icon,
  CheckmarkCircle01Icon,
  FolderLibraryIcon,
  Building06Icon,
  Alert02Icon,
  Activity01Icon,
  Target01Icon,
} from '@hugeicons/core-free-icons'
import { REFUND_PLURAL } from '@/features/finance/labels'

/** A capability string, or null when the item is always visible. */
export type Cap = string | null

export interface SecondaryItem {
  key: string
  label: string
  path: string
  cap?: Cap
  icon?: any
}

export interface PrimaryItem {
  key: string
  label: string
  path: string
  icon: any
  cap?: Cap
  secondary?: SecondaryItem[]
}

export const PRIMARY_NAV: PrimaryItem[] = [
  {
    key: 'home',
    label: 'Home',
    path: '/app',
    icon: Home01Icon,
    secondary: [
      { key: 'today', label: 'My day', path: '/app', icon: Calendar03Icon },
      { key: 'my_requests', label: 'My requests', path: '/app/requests', icon: ClipboardIcon },
      { key: 'overdue', label: 'Overdue', path: '/app/overdue', cap: 'project.view', icon: Alert02Icon },
      { key: 'approvals', label: 'Pending approvals', path: '/app/approvals', cap: 'task.approve', icon: CheckmarkCircle01Icon },
    ],
  },
  {
    key: 'projects',
    label: 'Projects',
    path: '/app/projects',
    icon: Layers01Icon,
    cap: 'project.view',
    secondary: [
      { key: 'all', label: 'All projects', path: '/app/projects', cap: 'project.view', icon: Layers01Icon },
      { key: 'portfolio', label: 'Portfolio', path: '/app/projects/portfolio', cap: 'portfolio.view', icon: Analytics01Icon },
      { key: 'templates', label: 'Templates (SOPs)', path: '/app/projects/templates', cap: 'project.view', icon: FolderLibraryIcon },
    ],
  },
  {
    key: 'mywork',
    label: 'My Work',
    path: '/app/work',
    icon: Task01Icon,
    secondary: [
      { key: 'today', label: 'Today', path: '/app/work', icon: Calendar03Icon },
      { key: 'assigned', label: 'Assigned to me', path: '/app/work/assigned', icon: Task01Icon },
    ],
  },
  {
    key: 'people',
    label: 'People',
    path: '/app/people',
    icon: UserMultipleIcon,
    cap: 'people.view',
    secondary: [
      { key: 'directory', label: 'Directory', path: '/app/people', cap: 'people.view', icon: UserMultipleIcon },
      { key: 'departments', label: 'Departments', path: '/app/people/departments', cap: 'people.manage', icon: UserGroupIcon },
      { key: 'leave', label: 'Leave', path: '/app/people/leave', cap: 'leave.request', icon: Calendar03Icon },
      { key: 'leave_calendar', label: 'Leave calendar', path: '/app/people/leave-calendar', cap: 'leave.calendar.view', icon: Calendar03Icon },
      { key: 'leave_approvals', label: 'Leave approvals', path: '/app/people/leave-approvals', cap: 'leave.approve', icon: ClipboardIcon },
      { key: 'audit', label: 'Audit feed', path: '/app/people/audit', cap: 'activity.view', icon: Activity01Icon },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    path: '/app/finance',
    icon: Wallet01Icon,
    cap: 'expense.submit',
    secondary: [
      { key: 'overview', label: 'Overview', path: '/app/finance', cap: 'budget.view', icon: Analytics01Icon },
      { key: 'budgets', label: 'Budgets', path: '/app/finance/budgets', cap: 'budget.view', icon: Coins01Icon },
      { key: 'variance', label: 'Variance report', path: '/app/finance/reports', cap: 'budget.view', icon: Analytics01Icon },
      { key: 'expenses', label: REFUND_PLURAL, path: '/app/finance/expenses', cap: 'expense.submit', icon: Invoice01Icon },
      { key: 'requisitions', label: 'Requisitions', path: '/app/finance/requisitions', cap: 'requisition.submit', icon: ClipboardIcon },
      // Plan 043 D4 — anyone can be named the owner of a plan line.
      { key: 'my_lines', label: 'My budget lines', path: '/app/finance/my-lines', cap: 'expense.submit', icon: Target01Icon },
      { key: 'fin_approvals', label: 'Approvals', path: '/app/finance/approvals', cap: 'expense.approve', icon: CheckmarkCircle01Icon },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    path: '/app/reports',
    icon: Analytics01Icon,
    secondary: [
      { key: 'mine', label: 'My reports', path: '/app/reports', icon: Analytics01Icon },
      { key: 'team', label: 'Team', path: '/app/reports/team', cap: 'reports.team', icon: UserGroupIcon },
      { key: 'org', label: 'Organization', path: '/app/reports/org', cap: 'reports.org', icon: Building06Icon },
    ],
  },
  {
    key: 'assistant',
    label: 'Assistant',
    path: '/app/assistant',
    icon: SparklesIcon,
    cap: 'ai.use',
  },
]

export const SETTINGS_ITEM: PrimaryItem = {
  key: 'settings',
  label: 'Settings',
  path: '/app/settings',
  icon: Settings01Icon,
}
